import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiUser } from 'react-icons/fi';
import { getUsers, createUser, updateUser, deleteUser } from '../../utils/journalApi';

const ROLES = [
  'gl_accountant', 'finance_manager', 'financial_controller',
  'cfo', 'finance_admin', 'internal_auditor',
];

const emptyForm = () => ({
  email: '', display_name: '', role: 'gl_accountant',
  company_codes: '', is_active: true,
});

export default function JournalUsers() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);  // null | 'new' | user-object
  const [form, setForm]         = useState(emptyForm());
  const [errors, setErrors]     = useState({});
  const [saving, setSaving]     = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await getUsers(); setUsers(r.data.items ?? r.data); } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyForm()); setErrors({}); setModal('new'); };

  const openEdit = (u) => {
    setForm({
      email:        u.email,
      display_name: u.display_name || '',
      role:         u.role,
      company_codes: Array.isArray(u.company_codes) ? u.company_codes.join(', ') : '',
      is_active:    u.is_active,
    });
    setErrors({});
    setModal(u);
  };

  const validate = () => {
    const e = {};
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.display_name.trim()) e.display_name = 'Required';
    if (!form.role) e.role = 'Required';
    return e;
  };

  const parseCodes = (s) =>
    s.split(/[,\s]+/).map(c => c.trim().toUpperCase()).filter(Boolean);

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      email:         form.email.trim(),
      display_name:  form.display_name.trim(),
      role:          form.role,
      company_codes: parseCodes(form.company_codes),
      is_active:     form.is_active,
    };

    setSaving(true);
    try {
      if (modal === 'new') {
        await createUser(payload);
        toast.success('User created.');
      } else {
        await updateUser(modal.id, {
          display_name:  payload.display_name,
          role:          payload.role,
          company_codes: payload.company_codes,
          is_active:     payload.is_active,
        });
        toast.success('User updated.');
      }
      setModal(null);
      load();
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data?.detail || {});
        toast.error('Please fix errors.');
      } else if (err.response?.status === 409) {
        setErrors({ email: 'Email already exists.' });
      }
    }
    setSaving(false);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await deleteUser(u.id);
      toast.success('User deleted.');
      load();
    } catch (_) {}
  };

  const f = (key, label, required = false, extra = {}) => (
    <div className="journal-field" key={key}>
      <label>{label}{required ? ' *' : ''}</label>
      <input
        value={form[key]}
        onChange={e => { setForm(p => ({ ...p, [key]: e.target.value })); setErrors(p => ({ ...p, [key]: '' })); }}
        className={errors[key] ? 'err' : ''}
        {...extra}
      />
      {errors[key] && <span className="journal-field-err">{errors[key]}</span>}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="journal-btn primary" onClick={openCreate}>
          <FiPlus size={14} /> New User
        </button>
      </div>

      <div className="journal-panel">
        <div className="journal-panel-head">
          <h3>User Management</h3>
          <span className="journal-muted" style={{ fontSize: '0.82rem' }}>{users.length} users</span>
        </div>

        {loading ? (
          <div className="journal-loading"><div className="journal-spinner" /><span>Loading…</span></div>
        ) : users.length === 0 ? (
          <div className="journal-empty">
            <FiUser size={32} style={{ color: '#c4cfdf', marginBottom: 8 }} />
            No users found.
          </div>
        ) : (
          <div className="journal-table-wrap">
            <table className="journal-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Role</th>
                  <th>Company Codes</th><th>Status</th><th>Created</th><th></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td><strong>{u.display_name || '—'}</strong></td>
                    <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{u.email}</td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 9px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        background: 'rgba(15,76,129,0.09)',
                        color: 'var(--jp)',
                        textTransform: 'capitalize',
                      }}>
                        {u.role?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="journal-muted">{Array.isArray(u.company_codes) ? u.company_codes.join(', ') : '—'}</td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 9px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        background: u.is_active ? '#dcfce7' : '#fee2e2',
                        color: u.is_active ? '#15803d' : '#b91c1c',
                      }}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ fontSize: 11 }} className="journal-muted">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--jp)', padding: 4 }}
                          onClick={() => openEdit(u)} title="Edit"
                        >
                          <FiEdit2 size={13} />
                        </button>
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}
                          onClick={() => handleDelete(u)} title="Delete"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal !== null && (
        <div className="j-modal-overlay" onClick={() => setModal(null)}>
          <div className="j-modal" onClick={e => e.stopPropagation()} style={{ width: 480 }}>
            <h3>{modal === 'new' ? 'New User' : `Edit — ${modal.email}`}</h3>

            <div className="journal-field-grid">
              {f('email',        'Email',        true,  { type: 'email', disabled: modal !== 'new', placeholder: 'name@savictech.com' })}
              {f('display_name', 'Display Name', true,  { placeholder: 'Full name' })}
            </div>

            <div className="journal-field-grid" style={{ marginTop: 12 }}>
              <div className="journal-field">
                <label>Role *</label>
                <select
                  value={form.role}
                  onChange={e => { setForm(p => ({ ...p, role: e.target.value })); setErrors(p => ({ ...p, role: '' })); }}
                  className={errors.role ? 'err' : ''}
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                  ))}
                </select>
                {errors.role && <span className="journal-field-err">{errors.role}</span>}
              </div>
              {f('company_codes', 'Company Codes', false, { placeholder: '1000, 2000' })}
            </div>

            <div className="journal-field" style={{ marginTop: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: 'var(--jp)' }}
                />
                <span style={{ fontSize: '0.87rem', fontWeight: 600, color: 'var(--j-txt)' }}>Active account</span>
              </label>
            </div>

            <div className="j-modal-footer">
              <button className="journal-btn ghost" onClick={() => setModal(null)}>
                <FiX size={14} /> Cancel
              </button>
              <button className="journal-btn primary" onClick={handleSave} disabled={saving}>
                <FiCheck size={14} /> {saving ? 'Saving…' : modal === 'new' ? 'Create User' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
